import React, { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card"
import { Button } from "@/shared/components/ui/button"
import { Badge } from "@/shared/components/ui/badge"
import { useToast } from "@/shared/components/Toast"
import { Bell, Check, Trash2, Loader2, Mail, MailOpen, AlertCircle, CheckCircle, Info } from "lucide-react"

interface Notification {
  id: number
  type: 'info' | 'success' | 'warning' | 'error'
  title: string
  message: string
  isRead: boolean
  link?: string
  createdAt: string
  readAt?: string
}

const Notifications: React.FC = () => {
  const { showToast } = useToast()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'unread'>('all')

  useEffect(() => {
    fetchNotifications()
  }, [filter])

  const fetchNotifications = async () => {
    setLoading(true)
    try {
      const url = filter === 'unread' ? '/api/notifications.php?unread=true' : '/api/notifications.php'
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()
      
      if (data.success) {
        setNotifications(data.data || [])
      } else {
        showToast({
          title: "Error",
          message: data.message || "Failed to fetch notifications",
          type: "error"
        })
      }
    } catch {
      showToast({
        title: "Error",
        message: "Failed to fetch notifications",
        type: "error"
      })
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (notificationId: number) => {
    try {
      const response = await fetch(`/api/notifications.php/${notificationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()
      
      if (data.success) {
        showToast({
          title: "Success",
          message: "Notification marked as read",
          type: "success"
        })
        fetchNotifications()
      }
    } catch {
      showToast({
        title: "Error",
        message: "Failed to mark notification as read",
        type: "error"
      })
    }
  }

  const deleteNotification = async (notificationId: number) => {
    try {
      const response = await fetch(`/api/notifications.php/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()
      
      if (data.success) {
        showToast({
          title: "Success",
          message: "Notification deleted",
          type: "success"
        })
        fetchNotifications()
      }
    } catch {
      showToast({
        title: "Error",
        message: "Failed to delete notification",
        type: "error"
      })
    }
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />
      default:
        return <Info className="h-5 w-5 text-blue-500" />
    }
  }

  const getNotificationBadgeColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-green-100 text-green-700'
      case 'error':
        return 'bg-red-100 text-red-700'
      case 'warning':
        return 'bg-yellow-100 text-yellow-700'
      default:
        return 'bg-blue-100 text-blue-700'
    }
  }

  const unreadCount = notifications.filter(n => !n.isRead).length

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Bell className="h-8 w-8 text-amber-600" />
            Notifications
            {unreadCount > 0 && (
              <Badge className="bg-amber-600 text-white">
                {unreadCount} New
              </Badge>
            )}
          </h1>
          <p className="text-gray-500 mt-2">Stay updated with your latest notifications</p>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          onClick={() => setFilter('all')}
          className={filter === 'all' ? 'bg-amber-600 hover:bg-amber-700' : ''}
        >
          <Mail className="h-4 w-4 mr-2" />
          All Notifications
        </Button>
        <Button
          variant={filter === 'unread' ? 'default' : 'outline'}
          onClick={() => setFilter('unread')}
          className={filter === 'unread' ? 'bg-orange-500 hover:bg-orange-600' : ''}
        >
          <MailOpen className="h-4 w-4 mr-2" />
          Unread ({unreadCount})
        </Button>
      </div>

      {/* Notifications List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-black" />
        </div>
      ) : notifications.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64">
            <Bell className="h-16 w-16 text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">No notifications found</p>
            <p className="text-gray-400 text-sm mt-2">
              {filter === 'unread' ? 'You have no unread notifications' : 'You have no notifications'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {notifications.map((notification) => (
            <Card 
              key={notification.id}
              className={`${!notification.isRead ? 'border-l-4 border-l-gray-500 bg-gray-50/30' : ''}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    {getNotificationIcon(notification.type)}
                    <div className="flex-1">
                      <CardTitle className="text-lg flex items-center gap-2">
                        {notification.title}
                        {!notification.isRead && (
                          <Badge variant="outline" className="text-xs bg-gray-100 text-black border-gray-300">
                            New
                          </Badge>
                        )}
                      </CardTitle>
                      <Badge className={`mt-2 text-xs ${getNotificationBadgeColor(notification.type)}`}>
                        {notification.type.toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!notification.isRead && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => markAsRead(notification.id)}
                        title="Mark as read"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteNotification(notification.id)}
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-sm">
                  {notification.message}
                </CardDescription>
                <div className="flex items-center justify-between mt-4">
                  <p className="text-xs text-gray-400">
                    {new Date(notification.createdAt).toLocaleString()}
                  </p>
                  {notification.link && (
                    <Button variant="link" size="sm" className="text-amber-600">
                      View Details →
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

export default Notifications
